import { MockAgentClient } from '@interaktiv/volto-ai-editor/api/MockAgentClient';
import type {
  SendMessageOptions,
  ToolCall,
  Turn,
} from '@interaktiv/volto-ai-editor/types';
import { buildScenario, validatePage } from './page';

/** The same interface as a future network client; all progress is local. */
export class LinoMockClient extends MockAgentClient {
  constructor(private readonly stepMs = 1500) {
    super();
  }

  async listSkills() {
    return [];
  }
  async listSkillGroups() {
    return [];
  }

  sendMessage(options: SendMessageOptions): Turn {
    const timers: ReturnType<typeof setTimeout>[] = [];
    let cancelled = false;
    const schedule = (delay: number, action: () => void) => {
      timers.push(
        setTimeout(() => {
          if (!cancelled) action();
        }, delay),
      );
    };
    const turn = {
      cancel: () => {
        cancelled = true;
        timers.forEach(clearTimeout);
      },
    };
    let response;
    try {
      response = buildScenario(options.message, options.state, options.context);
    } catch (error) {
      schedule(0, () =>
        options.onDone({
          newState: null,
          denied: false,
          error: { kind: 'failed', detail: String(error) },
        }),
      );
      return turn;
    }
    const denied = options.permission === 'read' && response.steps.length > 0;
    const steps = denied ? [] : response.steps;
    let elapsed = 0;
    let segment = 0;
    const stream = (text: string) => {
      const index = segment++;
      const words = text.split(' ');
      words.forEach((_, i) =>
        schedule(elapsed + i * 35, () =>
          options.onChunk?.(words.slice(0, i + 1).join(' '), index),
        ),
      );
      elapsed += words.length * 35 + 250;
    };
    stream(
      steps.length
        ? 'I will make these changes in your draft. You can stop me at any time.'
        : 'I am running locally with example responses.',
    );
    steps.forEach((step) => {
      elapsed += this.stepMs;
      schedule(elapsed, () => {
        validatePage(step.state);
        options.onToolCall({
          blocked: false,
          failed: false,
          before: null,
          after: null,
          image: null,
          ...step,
        } as ToolCall);
      });
    });
    elapsed += this.stepMs;
    stream(
      response.error
        ? 'The simulated run failed. You can undo any completed changes or try again.'
        : steps.length
          ? 'Done. The changes are in your draft; save when you are ready.'
          : 'Try “Add a paragraph”, “Rewrite selected text”, “Move this block down”, “Swap columns”, “Add a section”, or “Show the editing demo”.',
    );
    schedule(elapsed, () =>
      options.onDone({
        newState: steps.at(-1)?.state || null,
        denied,
        ...(response.error ? { error: { kind: 'failed' as const } } : {}),
      }),
    );
    return turn;
  }
}
